import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

const defaultSEO = {
  siteName: 'AI Film Society',
  siteUrl: 'https://aifilmsociety.org',
  defaultTitle: 'AI Film Society - Global Community for AI Filmmakers',
  defaultDescription: 'The global community for AI filmmakers. Discover films, connect with directors, attend events and workshops.',
  defaultImage: 'https://aifilmsociety.org/images/logo-circle-white.png',
  twitterHandle: '@aifilmsociety',
};

// Organization Schema
const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "AI Film Society",
  "alternateName": "AIFS",
  "url": "https://aifilmsociety.org",
  "logo": "https://aifilmsociety.org/images/logo-circle-white.png",
  "description": "The global community for AI filmmakers, enthusiasts, and industry pioneers exploring artificial intelligence and cinema.",
  "foundingDate": "2026",
  "founder": {
    "@type": "Person",
    "name": "Sam Joos",
    "jobTitle": "Founder",
    "description": "Award-winning commercial director and cinematographer"
  },
  "sameAs": [
    "https://twitter.com/aifilmsociety",
    "https://instagram.com/aifilmsociety",
    "https://youtube.com/aifilmsociety"
  ],
  "contactPoint": {
    "@type": "ContactPoint",
    "email": "sam@aiadstudio.com",
    "contactType": "customer service"
  }
};

// Website Schema
const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "name": "AI Film Society",
  "url": "https://aifilmsociety.org",
  "description": "Global community platform for AI filmmakers featuring films, events, workshops, and resources.",
  "potentialAction": {
    "@type": "SearchAction",
    "target": "https://aifilmsociety.org/search?q={search_term_string}",
    "query-input": "required name=search_term_string"
  }
};

export default function SEO({ 
  title, 
  description, 
  image, 
  url,
  type = 'website',
  noIndex = false,
  article = null,
  event = null
}) {
  const location = useLocation();
  
  const seoTitle = title 
    ? `${title} | AI Film Society` 
    : defaultSEO.defaultTitle;
  const seoDescription = description || defaultSEO.defaultDescription;
  const seoImage = image || defaultSEO.defaultImage;
  
  // Build canonical URL from current path - always use non-www version
  // This ensures Google knows the preferred version of each page
  const canonicalPath = location.pathname === '/' ? '' : location.pathname;
  const seoUrl = url || `${defaultSEO.siteUrl}${canonicalPath}`;

  // Build page-specific schema
  const pageSchemas = [organizationSchema, websiteSchema];

  // Add Article schema if provided
  if (article) {
    pageSchemas.push({
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": article.title,
      "description": article.description,
      "image": article.image,
      "datePublished": article.datePublished,
      "dateModified": article.dateModified || article.datePublished,
      "author": {
        "@type": "Organization",
        "name": "AI Film Society"
      },
      "publisher": {
        "@type": "Organization",
        "name": "AI Film Society",
        "logo": {
          "@type": "ImageObject",
          "url": defaultSEO.defaultImage
        }
      }
    });
  }

  // Add Event schema if provided
  if (event) {
    pageSchemas.push({
      "@context": "https://schema.org",
      "@type": "Event",
      "name": event.name,
      "description": event.description,
      "startDate": event.startDate,
      "endDate": event.endDate,
      "location": {
        "@type": "Place",
        "name": event.location,
        "address": event.address
      },
      "organizer": {
        "@type": "Organization",
        "name": "AI Film Society",
        "url": "https://aifilmsociety.org"
      }
    });
  }

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{seoTitle}</title>
      <meta name="description" content={seoDescription} />
      <link rel="canonical" href={seoUrl} />
      
      {/* Keywords - matching title terms */}
      <meta name="keywords" content="AI filmmaking, artificial intelligence cinema, AI films, AI directors, AI film community, AI filmmakers, machine learning movies, generative AI film, AI video production, future of filmmaking" />
      
      {/* Robots */}
      {noIndex && <meta name="robots" content="noindex, nofollow" />}
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={defaultSEO.siteName} />
      <meta property="og:title" content={seoTitle} />
      <meta property="og:description" content={seoDescription} />
      <meta property="og:image" content={seoImage} />
      <meta property="og:url" content={seoUrl} />
      <meta property="og:locale" content="en_US" />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={defaultSEO.twitterHandle} />
      <meta name="twitter:creator" content={defaultSEO.twitterHandle} />
      <meta name="twitter:title" content={seoTitle} />
      <meta name="twitter:description" content={seoDescription} />
      <meta name="twitter:image" content={seoImage} />
      
      {/* Additional Meta */}
      <meta name="theme-color" content="#9333EA" />
      <meta name="application-name" content={defaultSEO.siteName} />
      <meta name="author" content="AI Film Society" />
      <meta name="generator" content="React" />
      
      {/* Structured Data / JSON-LD */}
      <script type="application/ld+json">
        {JSON.stringify(pageSchemas)}
      </script>
    </Helmet>
  );
}
